import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import LoadingOverlay from "react-loading-overlay";
import NitroAdSidebar from "../../ads/NitroAdSidebar";
import { useContext, useEffect, useState } from "react";
import EnterpriseService from "../../common/services/enterprise-service";
import Paginator from "../../components/ui/Paginator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import config from "../../config";
import { getComponentsLimits } from "../../common/utils/DashboardUtils";
import { EnterpriseContext } from "../../components/enterprise/EnterpriseContext";
import DashboardTier from "../components/dashboardGallery/DashboardTier";
import BoxDynamicAd from "../../ads/BoxDynamicAd";

export default function DashboardsGallery({ container }) {
  const [dashboards, setDashboards] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 18,
  });
  const [totalPages, setTotalPages] = useState(0);
  const es = new EnterpriseService();
  const { globalContext } = useContext(EnterpriseContext);
  const [loading, setLoading] = useState(false);
  const [parameters, setParameters] = useState({
    tag: "",
    author: "",
    minComponents: 0,
    maxComponents: null,
    orderBy: "downloads",
  });

  useEffect(() => {
    getData();
  }, [pagination]);

  const getData = async () => {
    setLoading(true);
    const result = await es.getDashboards(parameters, pagination);
    setDashboards(result.data);
    setTotalPages(result.last_page);
    setLoading(false);
  };

  const handlePageClick = (event) => {
    setPagination({ ...pagination, page: event.selected + 1 });
  };

  const handleRowsPerPageChange = (perPage) => {
    setPagination({ ...pagination, page: 1, perPage: perPage });
  };

  return (
    <div>
      <div className="page-hero">
        <span className="page-hero-icon">
          <FontAwesomeIcon icon="dashboard" className="mx-0" />
        </span>
        <div>
          <h1 className="page-hero-title">Dashboards Gallery</h1>
        </div>
      </div>
      <LoadingOverlay
        active={loading}
        spinner={
          <Spinner color="primary" style={{ width: "4rem", height: "4rem" }} />
        }
      >
        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="filters-toolbar mb-3">
                  <FormGroup row className="mb-0">
                    <Col sm={2}>
                      <Label>{container.localeManager.strings.tag}</Label>
                      <input
                        className="form-control"
                        value={parameters.tag}
                        onChange={(e) => setParameters({ ...parameters, tag: e.target.value })}
                        placeholder="Filter by tag…"
                      />
                    </Col>
                    <Col sm={2}>
                      <Label>{container.localeManager.strings.author}</Label>
                      <input
                        className="form-control"
                        value={parameters.author}
                        onChange={(e) => setParameters({ ...parameters, author: e.target.value })}
                        placeholder="Filter by author…"
                      />
                    </Col>
                    <Col sm={2}>
                      <Label>Tier</Label>
                      <Input
                        type="select"
                        value={parameters.maxComponents === null ? "all" : String(parameters.maxComponents)}
                        onChange={(e) => {
                          const value = e.target.value;
                          setParameters({ ...parameters, minComponents: 0, maxComponents: value === "all" ? null : parseInt(value) });
                        }}
                      >
                        <option value="all">All Tiers</option>
                        <option value="5">Free (up to 5 components)</option>
                        <option value="20">Patron Fan (up to 20)</option>
                        <option value="999999">Patron Angel (unlimited)</option>
                      </Input>
                    </Col>
                    <Col sm={2}>
                      <Label>Order By</Label>
                      <Input
                        type="select"
                        value={parameters.orderBy}
                        onChange={(e) => setParameters({ ...parameters, orderBy: e.target.value })}
                      >
                        <option value="downloads">Downloads</option>
                        <option value="likes">Likes</option>
                        <option value="components_number">Components</option>
                      </Input>
                    </Col>
                    <Col className="d-flex align-items-end">
                      <Button color="primary" size="sm" onClick={getData}>
                        <FontAwesomeIcon icon="search" className="mx-0 me-1" />
                        {container.localeManager.strings.search}
                      </Button>
                    </Col>
                  </FormGroup>
                </div>

                <Paginator
                  rowsPerPageSelected={pagination.perPage}
                  handleRowsPerPageChange={handleRowsPerPageChange}
                  showRowsPerPage={true}
                  forcePage={pagination.page - 1}
                  totalPages={totalPages}
                  handlePageClick={handlePageClick}
                  rowsPerPage={[18, 36, 54, 72]}
                />

                {!loading && dashboards.length === 0 && (
                  <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 mt-3">
                    No dashboards found for the selected filters.
                  </div>
                )}

                {!loading && dashboards.length > 0 && (
                  <Row className="g-3 mt-1">
                    {dashboards.map((dashboard) => (
                      <Col lg="4" md="6" sm="12" key={dashboard.uuid}>
                        <div className="dashboard-gallery-card h-100" style={{ position: "relative" }}>
                          <DashboardTier dashboard={dashboard} />
                          <Link to={`/dashboards/gallery/${dashboard.uuid}`} className="dashboard-gallery-preview">
                            <img
                              src={dashboard.preview_image_url}
                              alt={dashboard.title}
                              className="dashboard-gallery-img"
                            />
                          </Link>
                          <div className="dashboard-gallery-body">
                            <Link to={`/dashboards/gallery/${dashboard.uuid}`} className="dashboard-gallery-title">
                              {dashboard.title}
                            </Link>
                            <div className="dashboard-gallery-meta">
                              <span className="text-muted small">
                                by <Link to={`/user/${dashboard.user.id}`}>{dashboard.user.name}</Link>
                              </span>
                            </div>
                            <div className="dashboard-gallery-stats">
                              <span><FontAwesomeIcon icon="cubes" className="mx-0 me-1" />{dashboard.components_number}</span>
                              <span><FontAwesomeIcon icon="download" className="mx-0 me-1" />{dashboard.downloads}</span>
                              <span><FontAwesomeIcon icon="thumbs-up" className="mx-0 me-1" />{dashboard.likes}</span>
                            </div>
                            {dashboard.tags && dashboard.tags.length > 0 && (
                              <div className="dashboard-gallery-tags">
                                {dashboard.tags.slice(0, 3).map((tag) => (
                                  <span key={tag} className="badge bg-primary me-1">{tag}</span>
                                ))}
                              </div>
                            )}
                            <div className="dashboard-gallery-actions">
                              <Link to={`/dashboards/gallery/${dashboard.uuid}`} className="btn btn-sm btn-secondary">
                                <FontAwesomeIcon icon="eye" className="mx-0" />
                              </Link>
                              <Button
                                size="sm"
                                color="success"
                                tag="a"
                                href={`${config.CLIENT_PROTOCOL_URL}://download-dashboard?uuid=${dashboard.uuid}`}
                                disabled={dashboard.components_number > getComponentsLimits(dashboard, container.dataService.data.patreonMember, globalContext.member)}
                              >
                                <FontAwesomeIcon icon="download" className="mx-0" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}

                <Paginator
                  rowsPerPageSelected={pagination.perPage}
                  handleRowsPerPageChange={handleRowsPerPageChange}
                  showRowsPerPage={true}
                  forcePage={pagination.page - 1}
                  totalPages={totalPages}
                  handlePageClick={handlePageClick}
                  rowsPerPage={[18, 36, 54, 72]}
                  className="mt-3"
                />
              </CardBody>
            </Card>
          </Col>
          <div className="ad-column-placeholder">
            {!container.dataService.data.enablePremiumFeature() && (
              <NitroAdSidebar container={container} />
            )}
          </div>
        </Row>
      </LoadingOverlay>
    </div>
  );
}
